/**
 * Datas do True Finance vivem como string `YYYY-MM-DD` no fuso America/Sao_Paulo.
 *
 * Usamos string em vez de `Date` porque a data de ocorrência de um lançamento é
 * um fato de calendário, não um instante: "gastei 150 hoje" é 03/08/2026 em São
 * Paulo, independente do fuso de quem consulta. Guardar `Date` convidaria o
 * clássico bug de o lançamento pular para o dia anterior ao ser serializado.
 */

export const TIMEZONE = 'America/Sao_Paulo';

/** Data de calendário no formato `YYYY-MM-DD`. */
export type DataISO = string;

/** Competência (mês contábil) no formato `YYYY-MM`. */
export type Competencia = string;

// en-CA formata como YYYY-MM-DD, que é exatamente o layout ISO que queremos.
const formatadorISO = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const formatadorHora = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Converte para meio-dia UTC. O horário do meio evita que qualquer conversão de
 * fuso (-03:00 no Brasil) empurre a data para o dia vizinho.
 */
function paraUTC(iso: DataISO): Date {
  const [ano, mes, dia] = iso.split('-').map(Number);
  return new Date(Date.UTC(ano ?? 0, (mes ?? 1) - 1, dia ?? 1, 12));
}

function deUTC(data: Date): DataISO {
  return data.toISOString().slice(0, 10);
}

/** Valida formato e existência real da data (rejeita `2026-02-30`). */
export function ehDataISO(valor: unknown): valor is DataISO {
  if (typeof valor !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(valor)) return false;
  return deUTC(paraUTC(valor)) === valor;
}

/** Data de hoje em São Paulo. */
export function hojeISO(agora: Date = new Date()): DataISO {
  return formatadorISO.format(agora);
}

/** Hora local de São Paulo, `HH:MM`. */
export function horaSP(instante: Date = new Date()): string {
  return formatadorHora.format(instante);
}

export function adicionarDias(iso: DataISO, dias: number): DataISO {
  const data = paraUTC(iso);
  data.setUTCDate(data.getUTCDate() + dias);
  return deUTC(data);
}

export function adicionarMeses(iso: DataISO, meses: number): DataISO {
  const data = paraUTC(iso);
  const diaOriginal = data.getUTCDate();
  data.setUTCDate(1);
  data.setUTCMonth(data.getUTCMonth() + meses);
  // 31/01 + 1 mês = 28/02 (ou 29 em bissexto), nunca 03/03.
  const ultimoDia = new Date(
    Date.UTC(data.getUTCFullYear(), data.getUTCMonth() + 1, 0, 12),
  ).getUTCDate();
  data.setUTCDate(Math.min(diaOriginal, ultimoDia));
  return deUTC(data);
}

export function inicioDoMes(iso: DataISO): DataISO {
  return `${iso.slice(0, 7)}-01`;
}

export function fimDoMes(iso: DataISO): DataISO {
  const [ano, mes] = iso.split('-').map(Number);
  const ultimo = new Date(Date.UTC(ano ?? 0, mes ?? 1, 0, 12)).getUTCDate();
  return `${iso.slice(0, 7)}-${String(ultimo).padStart(2, '0')}`;
}

/** Segunda-feira da semana da data informada (semana brasileira: seg–dom). */
export function inicioDaSemana(iso: DataISO): DataISO {
  const data = paraUTC(iso);
  const diaDaSemana = data.getUTCDay(); // 0=domingo
  const recuo = diaDaSemana === 0 ? 6 : diaDaSemana - 1;
  return adicionarDias(iso, -recuo);
}

/** Diferença em dias entre duas datas (b − a). */
export function diferencaEmDias(a: DataISO, b: DataISO): number {
  const MS_POR_DIA = 86_400_000;
  return Math.round((paraUTC(b).getTime() - paraUTC(a).getTime()) / MS_POR_DIA);
}

/** `2026-08-03` → `03/08/2026` */
export function formatarDataBR(iso: DataISO): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** `2026-08-03` → `08/2026` — a competência, exibida em IBM Plex Mono no painel. */
export function formatarCompetencia(iso: DataISO): string {
  const [ano, mes] = iso.split('-');
  return `${mes}/${ano}`;
}

const MESES_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

/** `2026-08-03` → `Agosto/2026` — cabeçalho dos relatórios do WhatsApp. */
export function nomeDoMes(iso: DataISO): string {
  const [ano, mes] = iso.split('-').map(Number);
  return `${MESES_PT[(mes ?? 1) - 1]}/${ano}`;
}
